import { computed, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useClientStore } from '@/stores/ClientStore'
import { useNotificationStore } from '@/stores/NotificationStore'
import { useSecureStore } from '@/stores/SecureStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { useProcedureStore } from '@/stores/ProcedureStore'
import { useProcedureExecutionStore } from '@/stores/ProcedureExecutionStore'
import { useMoney } from '@/composables/app/useMoney'
import { useProcedure, useProcedureExecution, useProcedureTransaction, useProcedureNavigation, useProcedureTransactionPaymentMethod } from '@/composables/modules/procedures'
import { useTamaro } from '@/vendors/integrations/tamaro'
import { useSentry } from '@/vendors/integrations'
import { default_config, available_payment_types } from '@/configurations/financials/tamaro'
import { scrollTo } from '@/utils/utils'
import { get as _get } from 'lodash'

export function useProcedureTamaro ({ procedure, procedure_execution }) {
  const { is_tamaro_loaded, loadTamaroWidget, destroyTamaroWidget, setTamaroAmount, setTamaroAmounts, setTamaroPaymentFormData, setTamaroPaymentFormStaticData, afterTamaroWidgetRender, beforeTamaroPaymentValidateAndSend, tamaroPaymentMethodChanged, tamaroPaymentComplete, tamaroSendPaymentError } = useTamaro()
  const sentry = useSentry()
  const { getAmount } = useMoney()
  const { getClient } = useClientStore()
  const { fetchProcedureSecureToken } = useSecureStore()
  const { postModalNotification } = useNotificationStore()
  const { getDocumentLanguage } = storeToRefs(useLanguageStore())
  const { currency, activeCurrencies } = storeToRefs(useCurrencyStore())
  const { loader: procedureExecutionLoader } = storeToRefs(useProcedureExecutionStore())
  const { loader: procedureLoader, error } = storeToRefs(useProcedureStore())
  const { id: transactionId, amount, frequency, handleCheckTransaction } = useProcedureTransaction({ procedure, procedure_execution })
  const { paymentMethod } = useProcedureTransactionPaymentMethod({ procedure_execution })
  const { slug: procedureSlug, externalReference, additionalExternalReference, targetId, targetType, isTamaroPaymentProvider } = useProcedure({ procedure })
  const { procedureExecution } = useProcedureExecution({ procedure, procedure_execution })
  const { nextRedirection } = useProcedureNavigation({ procedure, procedure_execution })

  const { client } = getClient()

  const transactionFormAmountBlock = computed(() => procedure.value?.blocks.transaction_form_amount)
  const frequencies = computed(() => procedure.value?.frequencies.map(frequency => _get(available_payment_types, frequency)).filter(v => v))
  const amounts = computed(() => _get(transactionFormAmountBlock.value, `attributes.${frequency.value}.amounts`, []).map(amount => getAmount({ amount: amount.value }) / 100))
  const tamaroExecution = computed(() => procedureExecution.value.to_tamaro)

  const config = computed(() => ({
    ...default_config,
    currencies: activeCurrencies.value.map(currency => currency.toLowerCase()),
    amounts: amounts.value,
    language: getDocumentLanguage,
    paymentTypes: frequencies.value,
    paymentFormPrefill: {
      amount: amount.value,
      currency: currency.value.toLowerCase(),
      stored_koa_target_id: targetId.value,
      stored_koa_target_type: targetType.value
    },
    [process.env.VUE_APP_TAMARO_ENV === 'prod' ? 'epms' : 'epmsStage']: client.value.tamaro,
    testMode: procedure.value?.is_draft
  }))

  const handleProcedureExecutionChange = () => {
    setTamaroAmounts(amounts.value)
    setTamaroAmount(amount.value)
    setTamaroPaymentFormData(tamaroExecution.value)

    setTamaroPaymentFormStaticData({
      'raisenow_parameters.fundraising_automation.attachment': JSON.stringify({
        ...procedure.value?.to_tamaro_automation,
        ...procedure_execution?.value.to_tamaro_automation,
        koa_goal_amount_subunit: getAmount({ amount: procedure.value.goal_amount }) / 100
      })
    })
  }

  const loadTamaro = async () => {
    if (is_tamaro_loaded.value) return

    try {
      await loadTamaroWidget(config.value)

      afterTamaroWidgetRender(() => {
        handleProcedureExecutionChange()

        setTamaroPaymentFormStaticData({
          payment_method: paymentMethod.value !== 'test_payment' ? paymentMethod.value : null,
          stored_campaign_id: externalReference.value,
          stored_campaign_subid: additionalExternalReference.value
        })
      })

      tamaroPaymentMethodChanged(payment_method => paymentMethod.value = payment_method)

      beforeTamaroPaymentValidateAndSend(() => setTamaroPaymentFormData(tamaroExecution.value))

      tamaroPaymentComplete(({ status, transaction_id }) => {
        if (transactionId.value === transaction_id) return

        if (['succeeded', 'active'].includes(status)) {
          handleCheckTransaction(transaction_id)
        } else {
          handleTamaroPaymentFailed()
        }

        procedureExecutionLoader.value = false

        fetchProcedureSecureToken({ id: procedureSlug })
      })

      tamaroSendPaymentError(() => {
        handleTamaroPaymentFailed()

        procedureExecutionLoader.value = false

        fetchProcedureSecureToken({ id: procedureSlug })

        sentry.error(Error('#001510 - Tamaro send payement failed'))
      })
    } catch {
      postModalNotification({ title: 'errors.an_error_occurred', code: '#001503', error: 'Tamaro failed to load', close: nextRedirection })
    }
  }

  const handleTamaroPaymentFailed = () => {
    error.value = 'errors.payment_failed'
    procedureLoader.value = false

    setTimeout(() => scrollTo('#errors'), 500)
  }

  watch(tamaroExecution, () => {
    if (isTamaroPaymentProvider.value) handleProcedureExecutionChange()
  }, { deep: true })

  onBeforeRouteLeave(() => destroyTamaroWidget())

  return {
    loadTamaro
  }
}